import PropTypes from "prop-types";
import React, { Component } from "react";
import { Grid, Box, CircularProgress, Typography } from "@material-ui/core";
import { withRouter } from "react-router-dom";
import moment from "moment";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import HeaderInfo from "../../waybee-ui/HeaderInfo/HeaderInfo";
import Group from "../../waybee-ui/Group";
import CollapseGroup from "../../waybee-ui/CollapseGroup";
import BackButton from "../../components/BackButton";
import StudentService from "../../services/StudentService";
import ResponsibleContact from "../StudentContact/components/ResponsibleContact";
import { Can } from "../../acl/can";

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 12px 0;
  border-bottom: 1px solid #ececec;

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled(Typography)`
  && {
    width: 220px;
    max-width: 40%;
    color: #828282;
    font-size: 15px;
    font-weight: 500;
  }
`;

const FieldValue = styled(Typography)`
  && {
    flex: 1;
    color: #1c1f74;
    font-size: 16px;
    font-weight: 500;
  }
`;

function formatAge(birthday, t) {
  if (!birthday) return null;
  const birth = moment(birthday);
  if (!birth.isValid()) return null;
  const years = moment().diff(birth, "years");
  if (years < 0) return null;
  return years === 1
    ? t("student.info.ageOne")
    : t("student.info.ageYears", { count: years });
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return value;
}

class StudentGeneralInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      student: null
    };
  }

  componentDidMount() {
    this.loadStudent();
  }

  loadStudent = async () => {
    const { match } = this.props;
    const { studentId } = match.params;
    try {
      const student = await StudentService.getStudentById(studentId);
      this.setState({ student });
    } catch (e) {
      console.error(e);
    }
  };

  renderField = (label, value) => (
    <FieldRow key={label}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue>{displayValue(value)}</FieldValue>
    </FieldRow>
  );

  render() {
    const { student } = this.state;
    const { match, t } = this.props;
    const { studentId } = match.params;

    if (!student) {
      return (
        <Box p={6} textAlign="center">
          <CircularProgress />
        </Box>
      );
    }

    const birthdayFormatted = student.birthday
      ? moment(student.birthday).format("DD/MM/YYYY")
      : null;
    const age = formatAge(student.birthday, t);
    const birthState = [student.birthState, student.birthStateName]
      .filter(Boolean)
      .join(" — ");
    const contacts = student.contacts || { resp: [], respTransport: [] };
    const respList = contacts.resp || [];
    const respTransport = contacts.respTransport || [];
    const birthDateValue = age
      ? t("student.info.birthDateWithAge", {
          date: birthdayFormatted,
          age
        })
      : birthdayFormatted;

    return (
      <Grid container spacing={2}>
        <BackButton to={`/student/${studentId}`} />
        <Grid item xs={12}>
          <Group>
            <Box mb={2}>
              <HeaderInfo
                name={student.name}
                info={t("student.raInfo", { ra: student.ra })}
                photo={student.photo}
              />
            </Box>
            <Box px={1} pb={2}>
              <Typography
                variant="h2"
                style={{ marginBottom: 16, color: "#1C1F74" }}
              >
                {t("student.menu.generalInfo")}
              </Typography>
              {this.renderField(t("student.info.academicRecord"), student.ra)}
              {this.renderField(t("common.labels.name"), student.name)}
              {this.renderField(t("student.info.birthState"), birthState)}
              {this.renderField(t("student.info.birthCity"), student.birthCity)}
              {this.renderField(t("student.info.birthDate"), birthDateValue)}
              {this.renderField(t("student.info.nickname"), student.nickname)}
              {this.renderField(t("student.info.surname"), student.surname)}
            </Box>
            <Can I="use module" a="studentContactInfo">
              <Box px={1} pb={2}>
                <Typography
                  variant="h2"
                  style={{ marginBottom: 8, marginTop: 8, color: "#1C1F74" }}
                >
                  {t("student.info.contact")}
                </Typography>
                <CollapseGroup title={t("student.info.more")}>
                  <ResponsibleContact
                    tel2={student.tel2}
                    tel1={student.tel}
                    email={student.email}
                    birthday={student.birthday}
                  />
                </CollapseGroup>
                <CollapseGroup title={t("student.info.guardians")}>
                  {respList.map(resp => (
                    <ResponsibleContact
                      key={`${resp.name}-${resp.relationship || ""}`}
                      name={resp.name}
                      relationship={resp.relationship}
                      tel2={resp.tel2}
                      tel1={resp.tel1}
                      email={resp.email}
                    />
                  ))}
                </CollapseGroup>
                {respTransport.length !== 0 && (
                  <CollapseGroup title={t("student.info.transportGuardians")}>
                    {respTransport.map(resp => (
                      <ResponsibleContact
                        key={`${resp.name}-${resp.relationship || ""}`}
                        name={resp.name}
                        relationship={resp.relationship}
                        tel2={resp.tel2}
                        tel1={resp.tel1}
                        email={resp.email}
                      />
                    ))}
                  </CollapseGroup>
                )}
              </Box>
            </Can>
          </Group>
        </Grid>
      </Grid>
    );
  }
}

StudentGeneralInfo.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      studentId: PropTypes.string
    })
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withRouter(withTranslation()(StudentGeneralInfo));
